import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { environment } from '@env/environment';
import { WhatsappSessionService } from '../../services/whatsapp-session.service';
import { ErrorScreenComponent } from 'app/shared/error-screen/error-screen.component';
import { LoaderService } from 'app/services/loader.service';
import { ModalComponent } from 'app/shared/modal/modal.component';

type PlatformKind = 'ios' | 'android' | 'other';

@Component({
  selector: 'app-whatsapp-pap',
  standalone: true,
  imports: [CommonModule, ErrorScreenComponent, ModalComponent],
  templateUrl: './whatsapp-pap.component.html',
})
export class WhatsappPapComponent implements OnInit {
  hash = '';
  rawPapUrl = '';
  titleError = '';
  descriptionError = '';
  leaving = false;

  showIntro = signal(false);
  showError = signal(false);
  showWhatsappWarning = signal(false);

  constructor(
    private route: ActivatedRoute,
    private whatsappSessionService: WhatsappSessionService,
    private loaderService: LoaderService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.hash = params.get('hash') ?? '';
      if (!this.hash) {
        this.failMessage('No encontramos el enlace de tu ahorro programado.', 'Enlace inválido');
        return;
      }

      if (this.isConsumed()) {
        this.redirectAway();
        return;
      }

      this.bootstrapFlow();
    });
  }

  openPap(): void {
    if (this.leaving || !this.rawPapUrl) return;
    this.leaving = true;
    this.markConsumed();
    this.clearPapData();
    window.location.href = this.rawPapUrl;
  }

  rejectPap(): void {
    if (this.leaving) return;
    this.leavePage();
  }

  getExternalRedirectUrl(): string {
    return `${window.location.origin}/app/whatsapp-pap/${this.hash}?from_redirect=true`;
  }

  openInExternalBrowser(): void {
    this.showWhatsappWarning.set(false);
    window.location.assign(this.getExternalRedirectUrl());
  }

  retry(): void {
    this.showError.set(false);
    this.bootstrapFlow();
  }

  isAndroidUA(): boolean {
    return this.platform() === 'android';
  }

  private bootstrapFlow(): void {
    if (this.isInAppBrowser()) {
      this.showWhatsappWarning.set(true);
      this.loaderService.hide();
      return;
    }

    this.loadPap();
  }

  private loadPap(): void {
    this.loaderService.show('Preparando tu ahorro programado...');
    this.whatsappSessionService.getPapWhatsapp(this.hash).subscribe({
      next: (response: any) => {
        const papUrl = this.resolvePapUrl(response?.papUrl);
        if (!papUrl) {
          this.failMessage('No fue posible abrir el ahorro programado. Intenta de nuevo o contacta a tu asesor.');
          return;
        }

        this.rawPapUrl = papUrl;
        this.loaderService.hide();
        this.showIntro.set(true);
      },
      error: (error) => {
        console.error(error);
        this.failMessage('No fue posible abrir el ahorro programado. El enlace puede estar vencido.');
      },
    });
  }

  private resolvePapUrl(sUrl: string): string {
    if (!sUrl) return '';
    const baseUrl = environment.papFincoAppUrl;
    if (!baseUrl) return sUrl;
    try {
      const original = new URL(sUrl);
      const target = new URL(baseUrl);
      target.search = original.search;
      return target.toString();
    } catch {
      return baseUrl;
    }
  }

  private failMessage(description: string, title = 'Error'): void {
    this.titleError = title;
    this.descriptionError = description;
    this.showError.set(true);
    this.loaderService.hide();
  }

  private storageKey(suffix: string): string {
    return `whatsapp_pap_${suffix}_${this.hash}`;
  }

  private isConsumed(): boolean {
    return localStorage.getItem(this.storageKey('consumed')) === 'true';
  }

  private markConsumed(): void {
    if (!this.hash) return;
    localStorage.setItem(this.storageKey('consumed'), 'true');
  }

  private clearPapData(): void {
    localStorage.removeItem('session');
    const storages = [localStorage, sessionStorage];
    for (const storage of storages) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (
          key &&
          key.startsWith('whatsapp_pap_') &&
          key.includes(this.hash) &&
          !key.startsWith(this.storageKey('consumed'))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => storage.removeItem(key));
    }
  }

  private redirectAway(): void {
    window.location.replace('https://www.google.com');
  }

  private leavePage(): void {
    this.leaving = true;
    this.markConsumed();
    this.clearPapData();
    try {
      window.close();
    } catch {
      // Algunos navegadores no permiten cerrar la pestaña.
    }
    this.redirectAway();
  }

  private isInAppBrowser(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
    const ua = userAgent.toLowerCase();
    return (
      ua.includes('whatsapp') ||
      ua.includes('fbav') ||
      ua.includes('fban') ||
      ua.includes('instagram')
    );
  }

  private platform(): PlatformKind {
    const userAgent = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
    if (/Android/i.test(userAgent)) return 'android';
    return 'other';
  }
}
